/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable no-nested-ternary */
import clsx from 'clsx';
import React, { type PropsWithChildren } from 'react';
import { Link as WouterLink } from 'wouter';

import type { KeyedRequired } from '@fragment/types';
import { List, ListItem, Marker } from '@fragment/ui/src/components/List';
import { strictClassNames } from '@fragment/ui/src/components/utils';

type NavTreeRenderer = {
  props: Omit<Parameters<typeof Link>[0], 'depth'>;
  onHover?: () => void;
};

type LeafNav = NavTreeRenderer & {
  subNav?: never;
  visibleOverride?: never;
};

export type SubTreeNav = NavTreeRenderer & {
  subNav: NavTree;
  // The ledgers and ledgers.accounts nav items should not show their subtree
  // when active since we don't know how to render the subnav tree URLs based
  // on our current route. This is because they render list pages which don't need
  // ledgerId or accountId to render.
  visibleOverride?: boolean;
};

export type NavTree<T extends string = string> = Record<
  T,
  LeafNav | SubTreeNav
>;

const NAV_TYPE_DEPTH_0 = ['numeric', 'ordered'] as const;
const NAV_TYPE_DEPTH_1 = ['upper-alphabetic', 'ordered'] as const;
const NAV_TYPE_DEPTH_2 = ['lower-alphabetic', 'ordered'] as const;
const NAV_TYPE_DEPTH_3_AND_GREATER = ['bullet', 'unordered'] as const;

const getNavTypeForDepth = (depth: number) => {
  switch (depth) {
    case 0:
      return NAV_TYPE_DEPTH_0;
    case 1:
      return NAV_TYPE_DEPTH_1;
    case 2:
      return NAV_TYPE_DEPTH_2;
    default:
      return NAV_TYPE_DEPTH_3_AND_GREATER;
  }
};

export const setActiveNavTreePath = (navTree: NavTree, active: string) => {
  const keyPath = active.split('.');
  let current: NavTree | undefined = navTree;
  keyPath.forEach((key) => {
    if (current && key in current) {
      current[key].props.active = true;
      current = current[key].subNav;
    } else {
      // This means we are rendering an invalid path (probably due to a change in routing)
      // This is probably suspense-related, so we don't want to throw an error here.
      current = undefined;
    }
  });
};

type LinkProps = KeyedRequired<
  Omit<JSX.IntrinsicElements['a'], 'color'>,
  'href'
> & {
  active?: boolean;
  depth: number;
};

const Link = ({
  href,
  depth,
  active,
  className,
  ...props
}: PropsWithChildren<LinkProps>) => (
  <WouterLink href={href}>
    <a
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      href={href}
      className={clsx(
        className,
        active || depth === 0 ? 'text-main' : 'text-main-500 hover:text-main',
        'group'
      )}
    />
  </WouterLink>
);

type Props = {
  listStyle?: 'ordered' | 'unordered';
  navTree: NavTree;
  depth?: number;
};

export const SideNav = ({ listStyle, navTree, depth = 0 }: Props) => {
  const [markerType, listType] = getNavTypeForDepth(depth);
  return (
    <List
      type={listStyle ?? listType}
      margin={depth === 0 ? undefined : 'ml-[2ch] mb-f2'}
    >
      {Object.entries(navTree).map(
        (
          [
            id,
            {
              subNav,
              visibleOverride,
              onHover,
              props: { children: linkChildren, className, ...otherLinkProps },
            },
          ],
          index
        ) => (
          <ListItem
            key={id}
            extraClassNames={strictClassNames(['min-w-f0', 'w-fit'])}
            data-testid={`${linkChildren}-nav-item`}
          >
            <Link
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...otherLinkProps}
              className={clsx(
                className,
                'flex flex-row item-start min-w-f0 w-full'
              )}
              data-testid="nav-item"
              depth={depth}
              onMouseOver={onHover}
            >
              <Marker
                flexItem="shrink-0"
                type={listStyle === 'unordered' ? 'bullet' : markerType}
                ordinal={index}
                margin="mr-[1ch]"
                color={
                  otherLinkProps.active
                    ? 'text-main'
                    : 'text-main-500 group-hover:text-main'
                }
              />
              <span className="min-w-f0 w-full truncate">{linkChildren}</span>
            </Link>
            {(visibleOverride ?? otherLinkProps.active) && subNav && (
              <SideNav navTree={subNav} depth={depth + 1} />
            )}
          </ListItem>
        )
      )}
    </List>
  );
};
